import { create } from 'zustand'
import type { Placement, Sighting, Species } from '../../content/types'
import { defaultBeastForm, defaultParts, defaultPlantForm, defaultPlantParts } from '../../content/partLibrary'

/**
 * 統一架構 — 無 map/explore 雙模式
 *
 * 高度圖不再存於 React 狀態。
 * 全部從 terrainBuffer 讀取，由 useFrame 驅動紋理更新。
 */

/** 攝影機模式 */
export type EditorMode = 'edit' | 'walk'

/** 地圖渲染風格 */
export type MapStyle = 'realistic' | 'qinglv' | 'shuimo'

/** 地形工具 */
export type TerrainTool = 'raise' | 'dig' | 'flatten' | 'smooth' | 'roughen'

/** 工具群組（取代分頁切換） */
export type ToolGroup = 'terrain' | 'object' | 'label' | 'beast' | 'plant'
export type MakerKind = 'beast' | 'plant'

/** 可放置物件類型 */
export type PlacedObjectType = 'tree_pine' | 'tree_broad' | 'tree_willow' | 'tree_hanlin' | 'tree_aspen' | 'tree_ash' | 'tree_crabclaw' | 'tree_broad_dot' | 'tree_bush' | 'rock' | 'bush' | 'cloud_fog'

/** 已放置物件 */
export interface PlacedObject {
  id: string
  type: PlacedObjectType
  position: [number, number, number]
  rotation: number
  scale: number
}

/** 地圖文字標籤 */
export interface MapLabel {
  id: string
  text: string
  position: [number, number, number]
}

interface GameState {
  // 地形數據就緒（僅翻轉一次，觸發 Canvas 顯示）
  heightmapReady: boolean
  setHeightmapReady: (ready: boolean) => void

  // 攝影機模式
  editorMode: EditorMode
  setEditorMode: (mode: EditorMode) => void

  // 地圖渲染風格
  mapStyle: MapStyle
  setMapStyle: (style: MapStyle) => void

  // 工具群組（取代 activeTab）
  toolGroup: ToolGroup
  setToolGroup: (group: ToolGroup) => void

  // 地形工具
  terrainTool: TerrainTool
  setTerrainTool: (tool: TerrainTool) => void

  // 筆刷參數
  brushRadius: number
  setBrushRadius: (r: number) => void
  brushStrength: number
  setBrushStrength: (s: number) => void

  // 指標鎖定
  isPointerLocked: boolean
  setPointerLocked: (locked: boolean) => void

  // 是否已進入行走世界（未點擊前不啟動控制器）
  isEnteredWorld: boolean
  setEnteredWorld: (entered: boolean) => void

  // WebGPU
  gpuError: string | null
  setGpuError: (error: string | null) => void

  // 物件放置
  placedObjects: PlacedObject[]
  addObject: (obj: PlacedObject) => void
  removeObject: (id: string) => void
  selectedObjectType: PlacedObjectType
  setSelectedObjectType: (type: PlacedObjectType) => void

  // 地圖文字標籤
  mapLabels: MapLabel[]
  addLabel: (label: MapLabel) => void
  removeLabel: (id: string) => void
  updateLabel: (id: string, text: string) => void
  updateLabelPosition: (id: string, position: [number, number, number]) => void

  worldId: string
  visitMode: boolean
  setVisitMode: (v: boolean) => void

  species: Species[]
  upsertSpecies: (s: Species) => void
  removeSpecies: (id: string) => void
  setSpeciesList: (list: Species[]) => void

  placements: Placement[]
  addPlacement: (p: Placement) => void
  removePlacement: (id: string) => void
  setPlacements: (list: Placement[]) => void

  draft: Species
  setDraft: (patch: Partial<Species>) => void
  plantDraft: Species
  setPlantDraft: (patch: Partial<Species>) => void
  setDraftPart: (slot: 'body' | 'head' | 'tail' | 'trunk' | 'leaf' | 'flower', source: string) => void

  makerKind: MakerKind
  setMakerKind: (k: MakerKind) => void
  makerOpen: boolean
  setMakerOpen: (open: boolean) => void
  placingBeast: boolean
  setPlacingBeast: (v: boolean) => void

  playerXZ: [number, number]
  setPlayerXZ: (xz: [number, number]) => void

  sightings: Sighting[]
  addSighting: (s: Sighting) => void
  setSightings: (list: Sighting[]) => void

  encounterToast: string | null
  setEncounterToast: (msg: string | null) => void

  studioWalk: boolean
  setStudioWalk: (v: boolean) => void
}

export const useGameStore = create<GameState>((set) => ({
  heightmapReady: false,
  setHeightmapReady: (ready) => set({ heightmapReady: ready }),

  editorMode: 'edit',
  setEditorMode: (mode) => set({ editorMode: mode }),

  mapStyle: 'qinglv',
  setMapStyle: (style) => set({ mapStyle: style }),

  toolGroup: 'terrain',
  setToolGroup: (group) => set({ toolGroup: group }),

  terrainTool: 'raise',
  setTerrainTool: (tool) => set({ terrainTool: tool }),

  brushRadius: 0.05,
  setBrushRadius: (r) => set({ brushRadius: r }),
  brushStrength: 0.5,
  setBrushStrength: (s) => set({ brushStrength: s }),

  isPointerLocked: false,
  setPointerLocked: (locked) => set({ isPointerLocked: locked }),

  isEnteredWorld: false,
  setEnteredWorld: (entered) => set({ isEnteredWorld: entered }),

  gpuError: null,
  setGpuError: (error) => set({ gpuError: error }),

  placedObjects: [],
  addObject: (obj) => set((s) => ({ placedObjects: [...s.placedObjects, obj] })),
  removeObject: (id) => set((s) => ({ placedObjects: s.placedObjects.filter(o => o.id !== id) })),
  selectedObjectType: 'tree_pine',
  setSelectedObjectType: (type) => set({ selectedObjectType: type }),

  mapLabels: [],
  addLabel: (label) => set((s) => ({ mapLabels: [...s.mapLabels, label] })),
  removeLabel: (id) => set((s) => ({ mapLabels: s.mapLabels.filter(l => l.id !== id) })),
  updateLabel: (id, text) => set((s) => ({
    mapLabels: s.mapLabels.map(l => l.id === id ? { ...l, text } : l),
  })),
  updateLabelPosition: (id, position) => set((s) => ({
    mapLabels: s.mapLabels.map(l => l.id === id ? { ...l, position } : l),
  })),

  worldId: crypto.randomUUID?.() ?? `w-${Date.now()}`,
  visitMode: false,
  setVisitMode: (v) => set({ visitMode: v }),

  species: [],
  upsertSpecies: (sp) => set((s) => ({
    species: s.species.some(x => x.id === sp.id)
      ? s.species.map(x => x.id === sp.id ? sp : x)
      : [...s.species, sp],
  })),
  removeSpecies: (id) => set((s) => ({
    species: s.species.filter(x => x.id !== id),
    placements: s.placements.filter(p => p.speciesId !== id),
  })),
  setSpeciesList: (list) => set({ species: list }),

  placements: [],
  addPlacement: (p) => set((s) => ({ placements: [...s.placements, p] })),
  removePlacement: (id) => set((s) => ({ placements: s.placements.filter(x => x.id !== id) })),
  setPlacements: (list) => set({ placements: list }),

  draft: {
    id: `sp:${Date.now().toString(36)}`,
    kind: 'monster',
    name: '未名之獸',
    parts: defaultParts(),
    beastForm: defaultBeastForm(),
    temperament: 'call',
    encounter: 'message',
    encounterMessage: '其音如嬰兒。',
  },
  setDraft: (patch) => set((s) => ({
    draft: {
      ...s.draft,
      ...patch,
      beastForm: patch.beastForm
        ? { ...defaultBeastForm(), ...s.draft.beastForm, ...patch.beastForm }
        : s.draft.beastForm,
    },
  })),
  plantDraft: {
    id: `sp:${Date.now().toString(36)}p`,
    kind: 'plant',
    name: '未名之木',
    parts: defaultPlantParts(),
    plantForm: defaultPlantForm(),
    temperament: 'ignore',
    encounter: 'appear',
  },
  setPlantDraft: (patch) => set((s) => ({
    plantDraft: {
      ...s.plantDraft,
      ...patch,
      plantForm: patch.plantForm
        ? { ...defaultPlantForm(), ...s.plantDraft.plantForm, ...patch.plantForm }
        : s.plantDraft.plantForm,
    },
  })),
  setDraftPart: (slot, source) => set((s) => {
    const key = s.makerKind === 'plant' ? 'plantDraft' : 'draft'
    const cur = s[key]
    const has = cur.parts.some(p => p.slot === slot)
    return {
      [key]: {
        ...cur,
        parts: has
          ? cur.parts.map(p => p.slot === slot ? { ...p, source } : p)
          : [...cur.parts, { slot, source }],
      },
    }
  }),

  makerKind: 'beast',
  setMakerKind: (k) => set({ makerKind: k }),
  makerOpen: false,
  setMakerOpen: (open) => set({ makerOpen: open }),
  placingBeast: false,
  setPlacingBeast: (v) => set({ placingBeast: v }),

  playerXZ: [0, 20],
  setPlayerXZ: (xz) => set({ playerXZ: xz }),

  sightings: [],
  addSighting: (sg) => set((s) => ({ sightings: [...s.sightings, sg] })),
  setSightings: (list) => set({ sightings: list }),

  encounterToast: null,
  setEncounterToast: (msg) => set({ encounterToast: msg }),

  studioWalk: false,
  setStudioWalk: (v) => set({ studioWalk: v }),
}))
